import { normalizeUsername, paths } from './paths';
import { readOptionalValue } from './rtdb';

/**
 * Le pseudo d'un membre, traduit en identifiant.
 *
 * Sert a faire un lien d'une mention ecrite a la main, y compris dans un
 * post publie avant que l'application sache le faire : l'affichage ne
 * peut pas compter sur la liste rangee a cote du texte, elle etait vide
 * a l'epoque.
 *
 * # Ce que cela coute
 *
 * Une lecture par pseudo, et une seule : la reponse est gardee pour la
 * session, y compris quand elle est negative — un « @ » decoratif ne
 * doit pas etre redemande a chaque defilement. Les demandes simultanees
 * du meme pseudo partagent la meme attente, sans quoi un fil citant
 * trois fois la meme personne en ferait trois.
 *
 * L'index est lisible pseudo par pseudo, jamais en entier : c'est ce que
 * disent les regles, et c'est ce qui permet de verifier qu'un pseudo est
 * libre avant meme d'avoir un compte.
 */

/** Ce qu'on sait deja : un identifiant, ou `null` si personne. */
const trouves = new Map<string, string | null>();
/** Ce qu'on est en train de demander. */
const enCours = new Map<string, Promise<string | null>>();

export function identifiantDuPseudo(nom: string): Promise<string | null> {
  const cle = normalizeUsername(nom);
  if (!cle) return Promise.resolve(null);

  const connu = trouves.get(cle);
  if (connu !== undefined) return Promise.resolve(connu);

  let attente = enCours.get(cle);
  if (!attente) {
    attente = readOptionalValue<string>(paths.username(cle))
      .catch(() => null)
      .then((valeur) => {
        const uid = typeof valeur === 'string' && valeur ? valeur : null;
        trouves.set(cle, uid);
        enCours.delete(cle);
        return uid;
      });
    enCours.set(cle, attente);
  }
  return attente;
}
